import { FC, useMemo } from 'react';
import { useGlobal } from 'hooks/useGlobal';
import { useRecoilValue } from 'recoil';
import compact from 'lodash/compact';

import * as K from 'common/constants';
import { HistogramData, QueryType } from 'common/graphqlTypes';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import DistributionGraph from 'components/common/Graphs/DistributionGraph';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import { searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import { plotDataRequestsState } from 'components/search/SearchLogsPage/SearchStateManager.state';
import { getLinkForRoute } from 'common/util/location';
import TimelineLegend from 'components/common/Legends/TimelineLegend';
import ELEMENT from 'common/element';
import { abbreviateNumber } from 'common/util/number';
import { getLrqErrorMessage, getRequestIds } from 'common/util/lrqRequest';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import DistibutionGraphLegend from './DistibutionGraphLegend';

import './styles.scss';

const prepareValues = (
  zeroCount: HistogramData['zeroCount'],
  positiveValues: HistogramData['positiveXAxis'],
  negativeValues: HistogramData['negativeXAxis'],
  defaultValue = zeroCount,
): Array<number> =>
  ([] as number[])
    .concat(negativeValues || [])
    .reverse()
    .concat(zeroCount ? [defaultValue] : [])
    .concat(positiveValues || []);

const prepareXValues: typeof prepareValues = (zeroCount, positiveXAxis, negativeXAxis) => prepareValues(zeroCount, positiveXAxis, negativeXAxis?.map(e => -e), 0);
const prepareYValues: typeof prepareValues = (zeroCount, positiveSampleCounts, negativeSampleCounts) => prepareValues(zeroCount, positiveSampleCounts, negativeSampleCounts);

const DistributionGraphResultsContainer: FC = () => {

  const searchParams = useRecoilValue(searchParamRecordState);
  const facet = searchParams[K.URL_PARAM.FACET] || '';
  const plotDataRequests = useRecoilValue(plotDataRequestsState);
  const plotDataIds = getRequestIds(plotDataRequests, QueryType.DISTRIBUTION);
  const result = useEventCache(plotDataIds);
  const data = result?.results?.[0]?.histogramData;
  const [ selectionRange ] = useGlobal(K.GLOBAL_KEY.CHART_SELECTION_RANGE);
  const { positiveXAxis, positiveSampleCounts, negativeXAxis, negativeSampleCounts, zeroCount } = data || {};
  const xValues = useMemo(() => prepareXValues(zeroCount, positiveXAxis, negativeXAxis), [zeroCount, positiveXAxis, negativeXAxis]);
  const yValues = useMemo(() => prepareYValues(zeroCount, positiveSampleCounts, negativeSampleCounts), [zeroCount, positiveSampleCounts, negativeSampleCounts]);

  const errors = compact(result?.errors);
  if (errors.length > 0) {
    const errorObject = {
      message: getLrqErrorMessage(errors),
      title: 'Invalid Search',
    };
    return <ErrorPlaceholder error={errorObject} />;
  }

  if (!result?.results?.[0]?.isDone) {
    return <LoadingPlaceholder offsetTop={0} />;
  }

  const logsPageLink = getLinkForRoute(K.ROUTE.EVENTS, searchParams);

  return (
    <div className="DistributionPage__Results">
      <div>
        <div className="DistributionGraph__Description">
          <TimelineLegend
            title="Value distribution for "
            facet={facet}
            buttonText="Show Logs"
            buttonLink={logsPageLink}
            buttonTestId={ELEMENT.timelineLegendShowLogs}
          />
          {selectionRange?.start &&  selectionRange?.end && (
            <div>
              <div>
                {`Selection: ${abbreviateNumber(Math.floor(selectionRange?.start))} → ${abbreviateNumber(
                  Math.floor(selectionRange?.end),
                )}`}
              </div>
              <div>{`Count: ${selectionRange.sum}`}</div>
            </div>
          )}
        </div>
        <DistributionGraph
          className="DistributionPage__Graph"
          xValues={xValues}
          yValues={yValues}
        />
      </div>
      <DistibutionGraphLegend
        median={data?.median}
        min={data?.min}
        max={data?.max}
        p10={data?.p10}
        p90={data?.p90}
        p99={data?.p99}
        p999={data?.p999}
        mean={data?.mean}
      />
    </div>
  );
};

export default DistributionGraphResultsContainer;
